import { Box, Grid } from '@mui/material'
import { differenceInCalendarDays, format, isWithinInterval } from 'date-fns'
import React, { useMemo, useState } from 'react'
import { WallChartEventBar } from './components'
import {WallChartEventBarProps, WallChartEventBarStylePositionProps } from './types'
import { WallChartItem } from '../../../types/wallchart'
import { WallChartTimespans } from '../WallChartTimespanSelector/types'
import { isRequestTypeVisible } from '../utils'
import { WallChartItemProps } from '../types'
import { getDateDiff } from '../../../utils/date-utils'
import { WallChartRowBackdrop } from '../WallChartBackdrop'

const barHeight = 10
const spacing = 8
const minHeight = 44

type PlottedWallChartItem = {
  event: WallChartItem
  position: {
    startPercent: string
    widthPercent: string
    extendedLeft: boolean
    extendedRight: boolean
    verticalOffset: number
  }
}

export const calculateRowHeight = (events: WallChartItem[], requestTypeFilters: any) => {
  const dayEventCounts: Record<string, number> = {}
  
  const visibleEvents = events.filter(event => isRequestTypeVisible(event.recordTypeStr, requestTypeFilters))

  visibleEvents.forEach(event => {
    const dateFrom = new Date(event.dateFrom)
    const dateTo = new Date(event.dateTo || event.dateFrom)

    for (let d = dateFrom; d <= dateTo; d.setDate(d.getDate() + 1)) {
      const dateKey = format(new Date(d), 'yyyy-MM-dd')
      dayEventCounts[dateKey] = (dayEventCounts[dateKey] || 0) + 1
    }
  })
  
  const maxEventsPerDay = Math.max(...Object.values(dayEventCounts).map(count => count), 0)
  const totalHeight = (barHeight * maxEventsPerDay) + (spacing * (maxEventsPerDay + 1))
  const finalHeight = Math.max(totalHeight, minHeight)

  return finalHeight
}

const plotBar = (item: WallChartItem, plottedEvents: PlottedWallChartItem[], timespanStartDate: Date, timespanEndDate: Date, timespanSegments: any): WallChartEventBarStylePositionProps => {
  const dateFrom = new Date(item.dateFrom)
  const dateTo = new Date(item.dateTo || item.dateFrom)
  const segmentPercent = (1 / timespanSegments.masterSegments) * 100
  const extendedLeft = format(dateFrom, 'yyyy-MM-dd') < format(timespanStartDate, 'yyyy-MM-dd')
  const extendedRight = format(dateTo, 'yyyy-MM-dd') > format(timespanEndDate, 'yyyy-MM-dd')

  const overlaps = plottedEvents.filter(plottedEvent => dateFrom <= plottedEvent.event.dateTo && dateTo >= plottedEvent.event.dateFrom)
  const maxVerticalOffset = Math.max(...overlaps.map(plottedEvent => plottedEvent.position.verticalOffset ?? 0))
  const verticalOffset = overlaps.length === 0 ? 0 : maxVerticalOffset + barHeight + spacing

  if (extendedLeft && extendedRight) {
    return { startPercent: '0%', widthPercent: '100%', extendedLeft, extendedRight, verticalOffset }
  }

  let diff = differenceInCalendarDays(dateFrom, timespanStartDate)
  const startPercent = extendedLeft ? 0 : diff * segmentPercent
  let endPercent = extendedRight ? 100 - startPercent : -1
  if (!extendedRight) {
    diff = extendedLeft
        ? differenceInCalendarDays(dateTo, timespanStartDate) + 1
        : differenceInCalendarDays(dateTo, dateFrom) + 1
    endPercent = timespanSegments.timespan === WallChartTimespans.DAY ? 100 : diff * segmentPercent
  }

  return {
    startPercent: `${startPercent}%`,
    widthPercent: `${endPercent}%`,
    extendedLeft,
    extendedRight,
    verticalOffset,
  }
}
function WallChartRowInner({
  timespanStartDate,
  timespanEndDate,
  timespanSegments,
  eventItem,
  popupUseOnClick = false,
  onItemInfo,
  onRowClick,
  requestTypeFilters,
  employeeLocationId,
  thresholdDates
}: WallChartEventBarProps) {
  const [hoverDate, setHoverDate] = useState<Date | undefined>()

  const plottedEvents = useMemo(() => {
    const visibleEvents: PlottedWallChartItem[] = []
    const allEvents: PlottedWallChartItem[] = []
    const sortedEvents = eventItem.items.sort(
      (a, b) => getDateDiff(a.dateFrom, a.dateTo, 'day') > getDateDiff(b.dateFrom, b.dateTo, 'day') ? -1 : 1)
    sortedEvents.forEach((item) => {
      if (!isWithinInterval(item.dateFrom, { start: timespanStartDate, end: timespanEndDate })
        && !isWithinInterval(item.dateTo, { start: timespanStartDate, end: timespanEndDate })) {
        return
      }
      const position = plotBar(item, visibleEvents, timespanStartDate, timespanEndDate, timespanSegments)
      if (isRequestTypeVisible(item.recordTypeStr, requestTypeFilters)) {
        visibleEvents.push({ event: item, position })
      }
      allEvents.push({ event: item, position })
    })
    
    return allEvents
  }, [eventItem, timespanStartDate, timespanEndDate, timespanSegments, requestTypeFilters])

  const totalHeight = Math.max(...plottedEvents.map(event => event.position.verticalOffset)) + (2 * spacing) + barHeight
  const finalHeight = Math.max(totalHeight, minHeight)

  const handleMouseMove = (e: React.MouseEvent, wcItem: WallChartItemProps, userDisplayName: string) => {
    const target = e.currentTarget as HTMLElement
    const width = target.offsetWidth || 0
    const { days } = wcItem
    const segs = width / days.length
    const index = Math.floor(e.nativeEvent.offsetX / segs)

    if (index < 0 || index >= days.length) {
      return
    }

    const hoveredDay = days[index].date
    if (hoveredDay === hoverDate) {
      return
    }

    if (!isRequestTypeVisible(wcItem.recordTypeStr, requestTypeFilters)) {
      return
    }

    setHoverDate(hoveredDay)
    onItemInfo?.(target, wcItem, userDisplayName, hoveredDay)
  }

  const handleMouseLeave = () => setHoverDate(undefined)

  return (
    <Grid container>
      <Grid item xs={12}>
        <Grid
          container
          position="relative"
          height={`${finalHeight}px`}
          sx={{
            transition: 'all 0.3s ease-in-out'
          }}
          data-testid={`${format(timespanStartDate, 'yyyy-MM-dd')} - ${format(timespanEndDate, 'yyyy-MM-dd')}`}
        >
          <WallChartRowBackdrop
            selectedTimeSpan={{
              startDate: timespanStartDate,
              endDate: timespanEndDate,
              segments: timespanSegments,
              directReport: false,
              rangeType: timespanSegments.timespan,
              title: ''
            }}
            thresholdDates={thresholdDates}
            employeeLocationId={employeeLocationId}
            rowHeight={finalHeight}
          />
          {plottedEvents
            .map(({ event: wcItem, position }, idx: number) => {
            const { userDisplayName } = eventItem
              return (
                <WallChartEventBar
                  key={`${wcItem.id}-${position.startPercent}`}
                  className={
                    wcItem.status?.toLowerCase() === 'pending' || (wcItem.isCancellation && wcItem.status?.toLowerCase() === 'change requested') ? 'selectedDate_pending' : ''
                  }
                  data-testid={`WallChartEvent-${wcItem.id}`}
                  wcitem={JSON.stringify({
                    userDisplayName,
                    ...wcItem,
                  })}
                  recordTypeStr={wcItem.recordTypeStr === 'Lieu Day' ? 'Lieu' : wcItem.recordTypeStr}
                  status={wcItem.status}
                  zIndex={110 + idx}
                  position={position}
                  isVisible={isRequestTypeVisible(wcItem.recordTypeStr, requestTypeFilters)}
                  style={{ top: `${spacing + position.verticalOffset}px` }}
                  onClick={e => {
                    if (!popupUseOnClick) {
                      return
                    }
                    onItemInfo?.(e.currentTarget, wcItem, userDisplayName, hoverDate)
                  }}
                  onMouseMove={e => handleMouseMove(e, wcItem, userDisplayName)}
                  onMouseLeave={handleMouseLeave}
                />
              )
          })}
          <Box borderBottom="1px solid #d1d1d1" width="100%" onClick={onRowClick} />
        </Grid>
      </Grid>
    </Grid>
  )
}

export const WallChartRow = React.memo(WallChartRowInner, (prevProps, nextProps) => (
    prevProps.eventItem === nextProps.eventItem &&
    prevProps.timespanStartDate === nextProps.timespanStartDate &&
    prevProps.timespanEndDate === nextProps.timespanEndDate &&
    prevProps.timespanSegments === nextProps.timespanSegments &&
    prevProps.requestTypeFilters === nextProps.requestTypeFilters &&
    prevProps.employeeLocationId === nextProps.employeeLocationId &&
    prevProps.thresholdDates === nextProps.thresholdDates &&
    prevProps.popupUseOnClick === nextProps.popupUseOnClick
))
